// src/components/Popover.jsx
//
// Popover — shared click-triggered dark popup (TOOLTIP-STANDARD).
//
// Unlike <Tooltip> (hover-only, presentational), Popover owns its dismiss
// behavior: while `open`, it listens for outside clicks, the Escape key,
// and re-clicks on its own trigger, calling `onClose` in all three cases.
// The caller still owns the `open` boolean (usually toggled by a click
// handler on the trigger) — Popover just guarantees consistent dismiss
// semantics and the canonical dark surface (T.tooltip) across the app.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   const triggerRef = useRef(null);
//   <button ref={triggerRef} onClick={() => setOpen(o => !o)}>trigger</button>
//   {open && (
//     <Popover
//       triggerRef={triggerRef}
//       onClose={() => setOpen(false)}
//       data-testid="my-popover"
//       style={{ top: "100%", right: 0 }}
//     >
//       Popover content
//     </Popover>
//   )}
//
// Props spread onto the rendered <span>:
//   triggerRef — ref to the element that opened the popover; clicks on it are
//                treated as "re-click to close", not "outside click"
//   onClose    — called on outside click, Escape, or trigger re-click
//   children   — popover content
//   style      — positioning + any style overrides merged over the defaults
//   …rest      — any other props (role, data-testid, etc.)

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { T } from "../app/tokens";

export function Popover({
  triggerRef,
  onClose,
  children,
  style,
  role = "dialog",
  variant = "tooltip",
  portalToBody = false,
  // Horizontal only: callers can opt in without changing vertical placement.
  horizontalViewportInset,
  ...rest
}) {
  const popoverRef = useRef(null);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const viewportInset = horizontalViewportInset ?? 8;

  useLayoutEffect(() => {
    if (!portalToBody) return undefined;

    function recomputePosition() {
      const trigger = triggerRef?.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) return;

      const triggerRect = trigger.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;
      const width = popoverRect.width || 268;
      const height = Math.min(
        popoverRect.height || 0,
        Math.max(0, viewportHeight - margin * 2),
      );
      const maxLeft = Math.max(viewportInset, viewportWidth - width - viewportInset);
      const left = Math.min(
        Math.max(viewportInset, triggerRect.right - width),
        maxLeft,
      );

      let top = triggerRect.bottom + 6;
      if (top + height > viewportHeight - margin) {
        top = triggerRect.top - height - 6;
      }
      const maxTop = Math.max(margin, viewportHeight - height - margin);
      top = Math.min(Math.max(margin, top), maxTop);

      setPortalPosition((current) => (
        current.top === top && current.left === left
          ? current
          : { top, left }
      ));
    }

    recomputePosition();
    // Opted-in menus must retain their inset when async content changes size.
    const observer = horizontalViewportInset != null && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(recomputePosition)
      : null;
    if (popoverRef.current) observer?.observe(popoverRef.current);
    window.addEventListener("scroll", recomputePosition, true);
    window.addEventListener("resize", recomputePosition);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", recomputePosition, true);
      window.removeEventListener("resize", recomputePosition);
    };
  }, [portalToBody, triggerRef, children, style, horizontalViewportInset, viewportInset]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    function handlePointerDown(e) {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef?.current?.contains(e.target)) {
        onClose?.();
        return;
      }
      onClose?.();
    }
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [onClose, triggerRef]);

  const defaultStyle = variant === "menu"
    ? {
        position: "absolute",
        zIndex: 30,
        background: T.card,
        color: T.dark,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: 0,
        boxShadow: T.shadowDropdown,
        fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontSize: T.fsSm5,
        fontWeight: T.fwRegular,
        lineHeight: 1.5,
      }
    : {
        position: "absolute",
        zIndex: 30,
        background: T.tooltip.bg,
        color: T.tooltip.fg,
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: T.tooltip.shadow,
        fontSize: T.fsSm,
        fontWeight: T.fwRegular,
        lineHeight: 1.5,
      };
  const popoverStyle = portalToBody
    ? {
        ...defaultStyle,
        ...style,
        position: "fixed",
        top: portalPosition.top,
        left: portalPosition.left,
        right: "auto",
        ...(variant === "menu"
          ? {
              zIndex: 1300,
              boxSizing: "border-box",
              minWidth: `min(268px, calc(100vw - ${viewportInset * 2}px))`,
              maxWidth: `calc(100vw - ${viewportInset * 2}px)`,
              maxHeight: "calc(100vh - 16px)",
              overflowY: "auto",
            }
          : {}),
      }
    : { ...defaultStyle, ...style };

  const popover = (
    <span
      ref={popoverRef}
      role={role}
      {...rest}
      style={popoverStyle}
    >
      {children}
    </span>
  );
  if (portalToBody && typeof document !== "undefined") {
    return ReactDOM.createPortal(popover, document.body);
  }

  return popover;
}
